import { useCombobox } from 'downshift';
import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { useConstant } from '../../../hooks/useConstant';
import { useTabs, type TabProps } from '../../Tabs';
import { ALL_TAB } from '../AutocompleteDropdown/types';
import { createCustomSortFilter } from '../SearchSelect/utils';
import { IGNORED_AUTOCOMPLETE_GROUP } from './tokens';
import type {
  AutocompleteGroup,
  AutocompleteSearchObject,
  AutocompleteSearchResult,
  SortFilterItem,
  UseAutocompleteOutput,
  UseAutocompleteProps,
} from './types';
import { getGroupsMetadata } from './utils';

export function getLabelWrapper<T>(getLabel: ((obj: T) => string | null | undefined) | undefined, obj: T | undefined) {
  if (obj == null) {
    return '';
  } else if (getLabel == null) {
    if (typeof obj === 'string') {
      return obj;
    } else {
      return JSON.stringify(obj);
    }
  } else {
    return getLabel(obj) ?? '';
  }
}

export function getDescriptionWrapper<T>(
  getDescription: ((obj: T) => string | null | undefined) | undefined,
  obj: T | undefined
): string {
  if (getDescription == null || obj == null) {
    return '';
  }
  return getDescription(obj) ?? '';
}

export function useAutocomplete<T>({
  items,
  getLabel,
  getDescription,
  isItemDisabled,
  getGroup,
  getTabGroup,
  initialSortByLabel = true,
  minMatchCharLength,
  itemSize,
  additionalSearchMatcher,
  sortFilterOverride,
  groupSorter,
  inputRef,
  ...props
}: UseAutocompleteProps<T>): UseAutocompleteOutput<T> {
  const isReadyRef = useRef(false);
  const [results, setResults] = useState<AutocompleteSearchResult<T>[]>([]);

  // Created once per component instance — computing locale collation tables on every comparison can be expensive.
  const collator = useMemo(() => new Intl.Collator(), []);

  const defaultSortFilter = useMemo(
    () => createCustomSortFilter<T>(undefined, additionalSearchMatcher),
    [additionalSearchMatcher]
  );
  const effectiveSortFilterOverride = useMemo(() => {
    if (sortFilterOverride) {
      return sortFilterOverride;
    }
    if (minMatchCharLength == null) {
      return defaultSortFilter;
    }
    return (fullList: SortFilterItem<T>[], searchString: string) =>
      searchString.length < minMatchCharLength ? fullList : defaultSortFilter(fullList, searchString);
  }, [sortFilterOverride, minMatchCharLength, defaultSortFilter]);

  // Store group metadata so filtered items can be reattached after searching.
  const groupMetadata = useMemo(() => {
    const itemsAlreadyGrouped = itemsAreAutocompleteGroups(items);
    if (itemsAlreadyGrouped) {
      return new Map(items.map(itemGroup => [itemGroup.group, itemGroup]));
    }
    return getGroupsMetadata(items, getGroup);
  }, [getGroup, items]);

  // After making sure all items are grouped, we sort the groups individually.
  const groups = useMemo(() => {
    const newGroups = [...groupMetadata.values()];
    if (initialSortByLabel) {
      return newGroups.map(group => {
        const keyed = group.items
          .map(item => ({ item, key: getLabelWrapper(getLabel, item) }))
          .toSorted((a, b) => collator.compare(a.key, b.key));
        return { ...group, items: keyed.map(({ item }) => item) };
      });
    }
    if (groupSorter) {
      return newGroups.toSorted(groupSorter);
    }
    return newGroups;
  }, [groupMetadata, initialSortByLabel, getLabel, collator, groupSorter]);

  const tabGroupItems = useMemo(() => {
    if (!getTabGroup) {
      return groups.filter(group => group.group !== IGNORED_AUTOCOMPLETE_GROUP).map(group => ({ group: group.group }));
    }

    const tabGroups = new Set<string>();
    for (const group of groups) {
      for (const item of group.items) {
        const tabGroup = getTabGroup(item);
        if (tabGroup && tabGroup !== IGNORED_AUTOCOMPLETE_GROUP) {
          tabGroups.add(tabGroup);
        }
      }
    }

    const items = [...tabGroups].map(group => ({ group }));
    return groupSorter ? items.toSorted(groupSorter) : items;
  }, [getTabGroup, groupSorter, groups]);

  const initialTabItems = useConstant([ALL_TAB, ...tabGroupItems]);
  const handleTabSelect = useCallback(() => {
    // Upon clicking a tab item, the input loses focus. Return focus to the input immediately.
    inputRef?.current?.focus();
  }, [inputRef]);

  // We now have grouped and sorted items. Using this, we can create tabs.
  const tabs = useTabs<TabProps & { group: string }>({
    initialItems: initialTabItems,
    showAddTab: false,
    initialSelectedIndex: 0,
    onSelect: handleTabSelect,
  });

  const { setItems: setTabItems } = tabs;

  // Keep the state of the tab items in check with the AutocompleteGroups we compute directly above.
  // We already set an initial set of tabs, so skip the initial render here by doing useUpdateEffect.
  useUpdateEffect(() => {
    const newTabItems = [ALL_TAB, ...tabGroupItems];
    setTabItems(prevTabItems => {
      if (!isEqual(prevTabItems, newTabItems)) {
        return newTabItems;
      }
      return prevTabItems;
    });
  }, [tabGroupItems, setTabItems]);

  // Given the selected tab ("selectedGroup"), we then filter down the groups to just that group.
  const renderedGroups = useMemo(() => {
    const selectedGroup = tabs.items[tabs.selectedIndex]?.group ?? ALL_TAB.group;
    // The All tab shows every group as-is. Items whose tab group differs from their list group
    // must still appear here, so we deliberately do not filter by getTabGroup on the All tab.
    if (selectedGroup === ALL_TAB.group) {
      return groups;
    }

    if (getTabGroup) {
      return groups
        .map(group => ({
          ...group,
          items: group.items.filter(item => getTabGroup(item) === selectedGroup),
        }))
        .filter(group => group.items.length > 0);
    }

    return groups.filter(group => group.group === selectedGroup);
  }, [tabs.items, tabs.selectedIndex, groups, getTabGroup]);

  // Re-attach filtered items to their groups.
  const { resultGroups, flattenedResults } = useMemo(() => {
    const resultsByGroup = results.reduce((map, result) => {
      if (map.has(result.item._group)) {
        map.get(result.item._group)!.push(result);
      } else {
        map.set(result.item._group, [result]);
      }

      return map;
    }, new Map<string, AutocompleteSearchResult<T>[]>());
    let resultGroups: AutocompleteGroup<AutocompleteSearchResult<T>>[] = [...resultsByGroup.entries()]
      .map(([groupKey, items]) => {
        const group = groupMetadata.get(groupKey);
        if (!group) {
          return undefined;
        }

        return {
          ...group,
          items,
        };
      })
      .compact();

    if (groupSorter) {
      resultGroups = resultGroups.toSorted(groupSorter);
    }

    // After re-merging all search results into groups, we need to proide downshift with a flat list of all our items.
    // This list should be of type T[]
    // It's important that we group them and then re-flatten because then we can guarantee the correct ordering of items over several groups
    const flattenedResults = resultGroups.reduce(
      (arr, group) => arr.concat(group.items.map(item => item.item.item)),
      [] as T[]
    );
    return { resultGroups, flattenedResults };
  }, [results, groupMetadata, groupSorter]);

  const combobox = useCombobox<T>({
    defaultHighlightedIndex: 0,
    // Prevent downshift from initialising inputValue to itemToString(selectedItem) on mount.
    // Search selects display the selected item via a separate trigger button, not by populating
    // the search input, so a non-empty initial inputValue would fire an expensive sortFilterOverride
    // call with the selection label before the dropdown has ever been opened.
    initialInputValue: '',
    items: flattenedResults,
    itemToString: item => {
      return item ? getLabelWrapper(getLabel, item) : '';
    },
    onInputValueChange: ({ inputValue }) => {
      if (inputValue == null) {
        inputValue = '';
      }
    },
    isItemDisabled: (item, index) => {
      // Two issues:
      // - due to the useUpdateEffect re-render, we need to check if the items are ready before we can check if they are disabled
      // - the 'item' checked could be undefined due to internal downshift behavior when the user is typing and the list is empty
      const listEmpty = flattenedResults.length === 0;
      return !isReadyRef.current || listEmpty ? true : (isItemDisabled?.(item, index) ?? false);
    },
    ...props,
  });

  // Pre-build SortFilterItem[] and a group lookup.
  // Memoized so it only rebuilds when items change, not on every keystroke.
  // The Map is keyed by SortFilterItem (object reference) rather than T, so primitive T values don't collide.
  const sortFilterData = useMemo(() => {
    const groupMap = new Map<SortFilterItem<T>, string>();
    const items = renderedGroups.flatMap(group =>
      group.items.map(item => {
        const label = getLabelWrapper(getLabel, item);
        const description = getDescriptionWrapper(getDescription, item);
        const entry: SortFilterItem<T> = {
          item,
          label,
          lowerLabel: label.toLowerCase(),
          description,
          lowerDescription: description.toLowerCase(),
        };
        groupMap.set(entry, group.group);
        return entry;
      })
    );
    return { items, groupMap };
  }, [renderedGroups, getLabel, getDescription]);

  useEffect(() => {
    const inputValue = combobox.inputValue ?? '';
    const searchString = inputValue.trim().toLowerCase();
    const filtered = searchString
      ? effectiveSortFilterOverride(sortFilterData.items, searchString)
      : sortFilterData.items;
    setResults(
      filtered.map(
        (sortItem): AutocompleteSearchResult<T> => ({
          item: {
            item: sortItem.item,
            label: sortItem.label,
            description: sortItem.description,
            disabled: isItemDisabled ? isItemDisabled(sortItem.item) : false,
            _group: sortFilterData.groupMap.get(sortItem) ?? IGNORED_AUTOCOMPLETE_GROUP,
          } satisfies AutocompleteSearchObject<T>,
        })
      )
    );
    isReadyRef.current = true;
  }, [combobox.inputValue, isItemDisabled, effectiveSortFilterOverride, sortFilterData]);
  return {
    groupMetadata,
    searchResults: results.map(item => item.item.item),
    items: flattenedResults,
    searchResultGroups: resultGroups,
    searchString: (combobox.inputValue ?? '').trim(),
    itemSize,
    isItemDisabled,
    tabs,
    ...combobox,
  };
}

export function itemsAreAutocompleteGroups<T>(items: T[] | AutocompleteGroup<T>[]): items is AutocompleteGroup<T>[] {
  if (items.length === 0) {
    return false; // inconclusive, assume false for this render
  }

  const firstItem = items[0];
  if (typeof firstItem !== 'object' || firstItem == null) {
    // If its not an object then its a string or a number meaning it cant be an autocompletegroup item
    return false;
  }

  return itemIsAutocompleteGroup(firstItem);
}

export function itemIsAutocompleteGroup<T>(item: T | AutocompleteGroup<T>): item is AutocompleteGroup<T> {
  return (
    item &&
    typeof item === 'object' &&
    ('items' satisfies keyof AutocompleteGroup<T>) in item &&
    ('group' satisfies keyof AutocompleteGroup<T>) in item
  );
}

/**
 * Takes an array of items, which can be whatever mix of T and AutocompleteGroup<T>, and flattens them to just T[]
 */
export function flattenAutocompleteGroupItems<T extends object>(items: (T | AutocompleteGroup<T>)[]): T[] {
  return items.flatMap(groupOrItem => {
    if (itemIsAutocompleteGroup(groupOrItem)) {
      return groupOrItem.items;
    } else {
      return groupOrItem;
    }
  });
}
